import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FileText, Clock, Award, Lock, PlayCircle, CheckCircle2, Languages, CalendarDays, Camera } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { payFranchiseOrder } from "@/lib/franchisePay";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function TestRow({ t, unlocked, onStart }) {
  const open = unlocked || t.free_preview;
  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-border py-4 last:border-0" data-testid={`ts-test-${t.id}`}>
      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-navy"><FileText size={18} /></div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold text-navy flex items-center gap-2">{t.title}{t.free_preview && !unlocked && <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">FREE</span>}</div>
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-1">
          <span>{t.question_count} Questions</span><span>{t.total_marks} Marks</span><span className="flex items-center gap-1"><Clock size={12} />{t.duration} Mins</span>
          {t.has_subjective && <span>Includes written answers</span>}
        </div>
      </div>
      {open ? <Button size="sm" className="viriks-amber text-white" onClick={() => onStart(t)} data-testid={`ts-start-${t.id}`}><PlayCircle size={15} className="mr-1" />Start Now</Button>
        : <Button size="sm" variant="outline" disabled data-testid={`ts-locked-${t.id}`}><Lock size={14} className="mr-1" />Locked</Button>}
    </div>
  );
}

export default function PackDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [owned, setOwned] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get(`/testseries/packs/${slug}`);
    setP(data);
    if (user) { const r = await api.get("/testseries/my/access"); setOwned(r.data.pack_ids.includes(data.id)); }
  }, [slug, user]);
  useEffect(() => { load().catch(() => setP(false)); }, [load]);

  const needLogin = () => navigate("/login", { state: { from: { pathname: `/tests/${slug}` } } });
  const unlock = async () => {
    if (!user) return needLogin();
    setBusy(true);
    try {
      if (!p.price) { await api.post(`/testseries/packs/${p.id}/enroll`); toast.success("Added to My Test Series"); }
      else {
        const { data } = await api.post(`/testseries/packs/${p.id}/pay`);
        const st = await payFranchiseOrder(data, { description: p.title, prefill: { name: user.name, email: user.email } });
        if (st === "paid") toast.success("Test series unlocked"); else if (st) toast.info("Payment is being confirmed");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const startTest = async (t) => {
    if (!user) return needLogin();
    if (!p.price && !owned) await api.post(`/testseries/packs/${p.id}/enroll`).catch(() => {});
    navigate(`/test-series/attempt/${t.id}`);
  };

  if (p === false) return <div className="py-24 text-center" data-testid="ts-pack-missing">Test series not found. <Link to="/tests" className="text-crimson underline">Browse all</Link></div>;
  if (!p) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 border-4 border-crimson border-t-transparent rounded-full animate-spin" /></div>;
  const unlocked = owned || !p.price;
  return (
    <div data-testid="ts-pack-page">
      <section className="bg-navy text-white"><div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div>
          <Link to="/tests" className="text-xs text-white/60 hover:text-white">Test Series</Link><span className="text-xs text-white/40"> / {p.exam?.name}</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-3" data-testid="ts-pack-title">{p.title}</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-sm">{p.description}</p>
          <div className="flex flex-wrap gap-5 mt-6 text-sm text-white/80">
            <span className="flex items-center gap-2"><FileText size={16} className="text-crimson" />{p.tests.length} Tests</span>
            <span className="flex items-center gap-2"><Languages size={16} className="text-crimson" />{(p.languages || []).join(", ")}</span>
            <span className="flex items-center gap-2"><CalendarDays size={16} className="text-crimson" />{p.validity_days} days validity</span>
            <span className="flex items-center gap-2"><Camera size={16} className="text-crimson" />Camera proctored</span>
          </div>
          {p.subjects?.length > 0 && <div className="flex flex-wrap gap-2 mt-5">{p.subjects.map((s) => <span key={s} className="text-xs border border-white/20 rounded-full px-3 py-1 text-white/80">{s}</span>)}</div>}
        </div>
        <div className="bg-white text-navy rounded-xl p-6" data-testid="ts-buy-box">
          {p.price > 0 ? <div className="flex items-end gap-2"><span className="font-heading text-3xl font-black">₹{p.price}</span>{p.mrp > p.price && <s className="text-muted-foreground mb-1">₹{p.mrp}</s>}<span className="text-xs text-muted-foreground mb-1.5">incl. GST</span></div>
            : <div className="font-heading text-3xl font-black text-emerald-700">FREE</div>}
          {owned || (!p.price && owned) ? <div className="mt-4 flex items-center gap-2 text-emerald-700 font-semibold text-sm" data-testid="ts-owned"><CheckCircle2 size={18} />You have access to this series</div>
            : <Button className="w-full mt-4 viriks-amber text-white font-semibold h-11" disabled={busy} onClick={unlock} data-testid="ts-unlock-btn">{p.price ? "Buy Now" : "Enroll for Free"}</Button>}
          <ul className="mt-5 space-y-2 text-sm text-navy/80">
            {["Exam-like interface with question palette", "Objective + written questions", "Rank, percentile and detailed solutions"].map((x) => <li key={x} className="flex gap-2"><Award size={15} className="text-crimson mt-0.5" />{x}</li>)}
          </ul>
        </div>
      </div></section>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <h2 className="font-heading font-bold text-navy text-base md:text-lg mb-2">Tests in this series</h2>
        <div className="bg-white border border-border rounded-xl px-5">
          {p.tests.length === 0 ? <p className="py-8 text-center text-muted-foreground">Tests will be added soon.</p> : p.tests.map((t) => <TestRow key={t.id} t={t} unlocked={unlocked} onStart={startTest} />)}
        </div>
      </div>
    </div>
  );
}
